<template>
  <div dir="rtl" class="min-h-screen bg-slate-50 flex items-center justify-center relative overflow-x-hidden font-sans text-slate-900 selection:bg-pink-200">

    <!-- Background Ambient Glows -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div class="absolute -top-40 -right-40 w-96 h-96 bg-pink-200/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-pulse" style="animation-duration: 4s;"></div>
      <div class="absolute top-40 -left-20 w-80 h-80 bg-purple-200/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-pulse" style="animation-duration: 5s; animation-delay: 1s;"></div>
    </div>

    <!-- Main Card Container -->
    <div class="relative z-10 w-full md:max-w-5xl md:h-[750px] flex flex-col md:flex-row bg-white md:rounded-[2.5rem] md:shadow-2xl md:border border-slate-100 overflow-hidden min-h-screen md:min-h-0">

      <!-- Right Side: Reset Form -->
      <div class="w-full md:w-1/2 flex flex-col px-6 py-10 sm:px-12 sm:py-12 bg-white relative">

        <!-- Native Back Button -->
        <div class="absolute top-6 right-6">
          <floating-button
            v-if="!showFloatingMan && isNative"
            icon="pi-angle-left"
            :insidePage="false"
            @clicked-floating-button="clickBack"
          />
        </div>

        <div class="flex flex-col w-full flex-grow max-w-sm mx-auto justify-center">

          <!-- Logo Header -->
          <div class="flex items-center gap-2 mb-10">
            <!-- Native Logo -->
            <img v-if="isNative" class="h-8" src="../../../assets/img/login/logo-sign.svg" alt="TimeIt Logo" />
            <!-- Web Premium Logo -->
            <div
              class="flex-shrink-0 flex items-center gap-2 cursor-pointer transition-transform hover:scale-105">
              <img src="../../../assets/img/login/logo-sign.svg" alt="" class="w-6 h-6">
              <span class="font-black text-2xl tracking-tight text-slate-800">time<span class="text-primary-600">it</span></span>
            </div>
          </div>

          <!-- Welcome Texts -->
          <h1 class="text-3xl sm:text-4xl font-black text-slate-900 mb-2 leading-tight">שכחת סיסמה?</h1>
          <p class="text-slate-500 mb-10">
            {{ !isResetPassword ? 'הכל בסדר, קורה לטובים ביותר 😉 נשלח אלייכם אימייל לאיפוס זריז.' : 'מעולה. נותר רק לבחור סיסמה חדשה והעסק שלכם שוב בידייכם.' }}
          </p>

          <!-- Email Step -->
          <div v-if="!isResetPassword" class="flex flex-col w-full space-y-6">
            <div class="flex flex-col gap-2">
              <label class="text-sm font-bold text-slate-700" for="username">כתובת אימייל</label>
              <IconField>
                <input-text
                  id="username"
                  v-model="email"
                  type="text"
                  placeholder="mail@example.com"
                  class="pr-10 w-full !bg-slate-50 !border !border-slate-200 !text-slate-900 !rounded-xl !py-3.5 focus:!outline-none focus:!border-pink-500 focus:!ring-1 focus:!ring-pink-500 transition-all text-left"
                  dir="ltr"
                />
                <InputIcon class="pi pi-at !text-slate-400" />
              </IconField>
            </div>

            <Button
              v-wave
              class="w-full mt-2 !bg-gradient-to-r !from-pink-500 !to-purple-600 hover:!from-pink-400 hover:!to-purple-500 !text-white !font-bold !py-4 !rounded-xl !border-0 transition-all !shadow-lg shadow-pink-500/30 transform hover:-translate-y-1 flex items-center justify-center"
              :loading="loading"
              @click="sendEmailClicked"
            >
              <span v-if="!loading" class="text-lg">שלחו לי אימייל לאיפוס</span>
            </Button>
          </div>

          <!-- New Password Step -->
          <div v-else class="flex flex-col w-full space-y-6">
            <div class="flex flex-col gap-2">
              <label class="text-sm font-bold text-slate-700" for="new-password">סיסמה חדשה</label>
              <p class="text-xs text-slate-500 mb-1">הסיסמה צריכה להכיל אותיות באנגלית, סימן ומספר.</p>
              <Password
                v-model="password"
                :feedback="false"
                toggleMask
                inputId="new-password"
                :pt="{
                  root: { class: 'w-full' },
                  input: { class: 'w-full !bg-slate-50 !border !border-slate-200 !text-slate-900 !rounded-xl !py-3.5 focus:!outline-none focus:!border-pink-500 focus:!ring-1 focus:!ring-pink-500 transition-all text-left' },
                  icon: { class: '!text-slate-400' }
                }"
                dir="ltr"
              />
            </div>

            <Button
              v-wave
              class="w-full mt-2 !bg-gradient-to-r !from-pink-500 !to-purple-600 hover:!from-pink-400 hover:!to-purple-500 !text-white !font-bold !py-4 !rounded-xl !border-0 transition-all !shadow-lg shadow-pink-500/30 transform hover:-translate-y-1 flex items-center justify-center"
              :loading="loading"
              @click="newPasswordEntered"
            >
              <span v-if="!loading" class="text-lg">אפסו לי את הסיסמה</span>
            </Button>
          </div>

          <!-- Back to Login -->
          <div class="mt-8 flex justify-center">
            <button @click="goToLogin" class="text-sm font-bold text-slate-500 hover:text-pink-600 transition-colors flex items-center gap-1">
              <ArrowRight class="w-4 h-4" /> חזרה לעמוד ההתחברות
            </button>
          </div>

        </div>

        <!-- Mobile Register Link -->
        <div v-if="showRegister && !showFloatingMan" class="w-full flex justify-center items-center mt-auto pt-10">
          <div class="px-5 py-3 flex gap-2 rounded-xl bg-slate-50 border border-slate-100 text-sm shadow-sm">
            <span class="text-slate-600">אין לך משתמש?</span>
            <router-link class="text-pink-600 font-bold hover:underline" :to="{ name: AuthRouteNames.Register }">
              להרשמה בחינם
            </router-link>
          </div>
        </div>
      </div>

      <!-- Left Side: Premium Visuals (Desktop Only) -->
      <div v-if="showFloatingMan" class="hidden md:flex w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-purple-900 p-12 flex-col justify-between relative overflow-hidden text-white">

        <!-- Abstract Decorations -->
        <div class="absolute top-0 right-0 w-64 h-64 bg-purple-500/20 rounded-full blur-[80px]"></div>
        <div class="absolute bottom-0 left-0 w-80 h-80 bg-orange-500/20 rounded-full blur-[80px]"></div>

        <!-- Text Content -->
        <div class="z-10 relative mt-8">
          <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm text-pink-200 text-xs font-bold border border-white/10 mb-6">
            <Sparkles class="w-3.5 h-3.5" />
            העסק שלך לא עוצר לרגע
          </div>
          <h2 class="text-4xl lg:text-5xl font-black leading-tight mb-4 tracking-tight">
            כמה קליקים, ואתם בחזרה בעניינים.
          </h2>
          <p class="text-slate-300 text-lg leading-relaxed max-w-sm">
            אפסו את הסיסמה ותחזרו מיד למערכת שמנהלת לכם את היומן, שולחת תזכורות במקומכם ומגדילה את ההכנסות.
          </p>
        </div>

        <!-- Visual Representation -->
        <div class="z-10 relative flex-1 flex flex-col items-center justify-center mt-10 space-y-5">
          <div class="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-4 w-full max-w-sm transform transition-transform hover:-translate-y-2 duration-500 shadow-2xl relative overflow-hidden">
            <div class="absolute left-0 top-0 bottom-0 w-1 bg-purple-400"></div>
            <div class="flex items-center gap-4">
              <div class="bg-purple-400/20 p-3 rounded-xl text-purple-300">
                <Clock class="w-6 h-6" />
              </div>
              <div>
                <p class="text-white font-bold text-sm">רשימת ההמתנה עבדה בשבילך</p>
                <p class="text-slate-300 text-xs mt-0.5">2 תורים שהתפנו התמלאו אוטומטית</p>
              </div>
            </div>
          </div>

          <div class="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-4 w-full max-w-sm transform -translate-x-6 transition-transform hover:-translate-y-2 duration-500 shadow-2xl relative overflow-hidden">
            <div class="absolute left-0 top-0 bottom-0 w-1 bg-orange-400"></div>
            <div class="flex items-center gap-4">
              <div class="bg-orange-400/20 p-3 rounded-xl text-orange-300">
                <Gift class="w-6 h-6" />
              </div>
              <div>
                <p class="text-white font-bold text-sm">הכנסה נוספת בזמן שנעדרת</p>
                <p class="text-slate-300 text-xs mt-0.5">לקוחה רכשה גיפט-קארד לפני שעה</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Desktop Register Link -->
        <div class="z-10 relative mt-12">
          <div class="bg-white/5 backdrop-blur-md rounded-2xl p-5 border border-white/10 flex justify-between items-center">
            <div>
              <p class="font-bold text-white mb-0.5">עדיין אין לך משתמש?</p>
              <p class="text-sm text-slate-400">בואי לנסות אותנו בחודש חינם.</p>
            </div>
            <router-link
              class="bg-white text-slate-900 hover:bg-slate-100 font-bold py-3 px-6 rounded-xl transition-colors shadow-lg text-sm"
              :to="{ name: AuthRouteNames.Register }">
              הרשמה למערכת
            </router-link>
          </div>
        </div>

      </div>

    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted } from 'vue';
import { sendPasswordResetEmail, submitNewPassword } from 'supertokens-web-js/recipe/emailpassword';
import Session from 'supertokens-web-js/recipe/session';
import { useToast } from 'vue-toastification';
import { AdminRouteNames, AuthRouteNames } from '@app/router/route-names';
import { useNavigation } from "@app/composables/navigation";
import { Capacitor } from '@capacitor/core';
import { useResponsive } from "@app/composables/responsive";
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import Password from 'primevue/password';
import { useRoute, useRouter } from "vue-router";
import * as EmailValidator from 'email-validator';
import { trackEvent } from '@app/composables/posthog';

// Icons
import {
  ArrowRight,
  Gift,
  Clock,
  Sparkles
} from 'lucide-vue-next';

const { isMobile, screenWidth } = useResponsive();
const toast = useToast();
const { goTo } = useNavigation();
const route = useRoute();
const router = useRouter();

const loading = ref(false);
const email = ref('');
const password = ref('');

const isNative = computed(() => Capacitor.isNativePlatform());
const isResetPassword = route.query.token as string;
const showRegister = computed(() => {
  return isMobile.value; // fixed to .value
});

const sendEmailClicked = async () => {
  trackEvent('reset_password_email_clicked');
  if (!email.value || (email.value.includes('gmail.com') && email.value.includes('+') ? false : !EmailValidator.validate(email.value))) {
    toast.error('יש להזין כתובת אימייל תקינה')
    return;
  }
  loading.value = true;
  try {
    const response = await sendPasswordResetEmail({
      formFields: [{
        id: "email",
        value: email.value
      }]
    });

    if (response.status === "FIELD_ERROR") {
      response.formFields.forEach(formField => {
        if (formField.id === "email") {
          console.log(formField.error)
          toast.error('לא הצלחנו לשלוח את המייל, נראה שיש בעיה עם כתובת המייל שהוזנה')
        }
      })
    } else {
      toast.success('מייל לאיפוס הסיסמה נשלח אליכם ברגעים אלה :)')
    }
  } catch (err: any) {
    if (err.isSuperTokensGeneralError === true) {
      console.log(err.message)
    }
    toast.error('לא הצלחנו לשלוח את המייל, אנא נסו שוב')
  }
  loading.value = false;
}

const newPasswordEntered = async () => {
  trackEvent('reset_password_submitted');
  if (password.value?.length < 3) {
    toast.error('יש להזין סיסמה תקינה')
    return;
  }

  loading.value = true;
  try {
    const response = await submitNewPassword({
      formFields: [{
        id: "password",
        value: password.value
      }]
    });

    if (response.status === "FIELD_ERROR") {
      response.formFields.forEach(formField => {
        if (formField.id === "password") {
          console.log(formField.error)
          toast.error('לא הצלחנו לאפס את הסיסמה, נראה שיש בעיה עם הסיסמה שהוזנה')
        }
      })
    } else if (response.status === "RESET_PASSWORD_INVALID_TOKEN_ERROR") {
      toast.error('לא הצלחנו לאפס את הסיסמה, אנא נסו שוב')
      setTimeout(() => {
        router.push({name: AuthRouteNames.AdminLogin})
      }, 750)
    } else {
      toast.success('הסיסמה אופסה בהצלחה :)')
      setTimeout(() => {
        router.push({name: AuthRouteNames.AdminLogin})
      }, 750)
    }
  } catch (err: any) {
    if (err.isSuperTokensGeneralError === true) {
      console.log(err.message)
    }
    toast.error('לא הצלחנו לאפס את הסיסמה, אנא נסו שוב')
  }
  loading.value = false;
}

const goToLogin = () => {
  goTo(AuthRouteNames.AdminLogin);
};

const checkForSession = async () => {
  if (await Session.doesSessionExist()) {
    goTo(AdminRouteNames.Home);
  }
};

const showFloatingMan = computed(() => {
  return screenWidth.value > 768;
});

const clickBack = () => {
  goTo(AuthRouteNames.CustomerLogin);
};

onMounted(() => {
  checkForSession();
});
</script>
